import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AddressInput } from '@/components/shared/AddressInput';
import {
  useCreateBillingAddress,
  useUpdateBillingAddress,
} from '@/hooks/useBillingAddresses';
import type { BillingAddress } from '@jobos/shared/types/billingAddress';
import type { Manager } from '@jobos/shared/types/manager';
import { formatManagerName } from '@jobos/shared';

const billingAddressFormSchema = z.object({
  label: z.string().optional(),
  managerId: z.string().optional(),
  addressLine1: z.string().optional(),
  addressLine2: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip: z.string().optional(),
});

type BillingAddressFormData = z.infer<typeof billingAddressFormSchema>;

interface BillingAddressModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  managers: Manager[];
  editingAddress?: BillingAddress | null;
}

export function BillingAddressModal({
  open,
  onOpenChange,
  clientId,
  managers,
  editingAddress = null,
}: BillingAddressModalProps) {
  const { mutateAsync: createAddress, isPending: isCreating } =
    useCreateBillingAddress();
  const { mutateAsync: updateAddress, isPending: isUpdating } =
    useUpdateBillingAddress(clientId);

  const isPending = isCreating || isUpdating;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
  } = useForm<BillingAddressFormData>({
    resolver: zodResolver(billingAddressFormSchema),
    defaultValues: {
      label: '',
      managerId: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zip: '',
    },
  });

  const addressLine1 = watch('addressLine1');
  const addressLine2 = watch('addressLine2');
  const city = watch('city');
  const state = watch('state');
  const zip = watch('zip');
  const managerId = watch('managerId');

  // Reset form when modal opens/closes or editing address changes
  useEffect(() => {
    if (open) {
      if (editingAddress) {
        reset({
          label: editingAddress.label || '',
          managerId: editingAddress.managerId || '',
          addressLine1: editingAddress.addressLine1 || '',
          addressLine2: editingAddress.addressLine2 || '',
          city: editingAddress.city || '',
          state: editingAddress.state || '',
          zip: editingAddress.zip || '',
        });
      } else {
        reset({
          label: '',
          managerId: '',
          addressLine1: '',
          addressLine2: '',
          city: '',
          state: '',
          zip: '',
        });
      }
    }
  }, [open, editingAddress, reset]);

  const onSubmit = async (data: BillingAddressFormData) => {
    try {
      const payload = {
        clientId,
        label: data.label?.trim() || undefined,
        managerId: data.managerId || undefined,
        addressLine1: data.addressLine1?.trim() || undefined,
        addressLine2: data.addressLine2?.trim() || undefined,
        city: data.city?.trim() || undefined,
        state: data.state?.trim() || undefined,
        zip: data.zip?.trim() || undefined,
      };

      if (editingAddress) {
        await updateAddress({
          id: editingAddress.id,
          data: payload,
        });
      } else {
        await createAddress(payload);
      }

      onOpenChange(false);
    } catch (error) {
      console.error('Failed to save billing address:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {editingAddress ? 'Edit Billing Address' : 'Add Billing Address'}
          </DialogTitle>
          <DialogDescription>
            {editingAddress
              ? 'Update the billing address information below.'
              : 'Add a new billing address for this client.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Label
              </label>
              <Input
                {...register('label')}
                placeholder="e.g. Main Office, PO Box"
              />
              <p className="text-xs text-gray-500 mt-1">
                Optional label to identify this address
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Manager
              </label>
              <Select
                value={managerId || ''}
                onValueChange={(value: string) =>
                  setValue('managerId', value === 'none' ? '' : value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="None (client-level address)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    None (client-level address)
                  </SelectItem>
                  {managers.map((manager) => (
                    <SelectItem key={manager.id} value={manager.id}>
                      {formatManagerName(
                        manager.name,
                        manager.firstName,
                        manager.lastName
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Optionally associate this address with a manager
              </p>
            </div>

            <AddressInput
              value={{
                addressLine1,
                addressLine2,
                city,
                state,
                zip,
              }}
              onChange={(address) => {
                setValue('addressLine1', address.addressLine1 || '');
                setValue('addressLine2', address.addressLine2 || '');
                setValue('city', address.city || '');
                setValue('state', address.state || '');
                setValue('zip', address.zip || '');
              }}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending
                ? 'Saving...'
                : editingAddress
                  ? 'Update Address'
                  : 'Create Address'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
